import { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { NAV_ITEMS } from "@/lib/nav";
import { Menu, LogOut, Heart, ChevronDown, Bell } from "lucide-react";
import { api } from "@/lib/api";
import { resolveMedia } from "@/lib/api";
import { useBrand } from "@/lib/brand";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";

const ROLE_LABEL = {
  SUPER_ADMIN: "Super Admin", ADMIN: "Admin", STAFF: "Staff", DRIVER: "Driver",
};

function NavList({ onNavigate }) {
  const { hasPerm } = useAuth();
  const items = NAV_ITEMS.filter((i) => hasPerm(i.perm));
  return (
    <nav className="flex flex-col gap-0.5 px-3 py-2 no-scrollbar overflow-y-auto" data-testid="sidebar-nav">
      {items.map((item) => {
        const Icon = item.icon;
        return (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            onClick={onNavigate}
            data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                isActive
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
              }`
            }
          >
            <Icon className="w-[18px] h-[18px] shrink-0" />
            <span>{item.label}</span>
          </NavLink>
        );
      })}
    </nav>
  );
}

function Brand() {
  const brand = useBrand();
  return (
    <div className="flex items-center gap-3 px-5 h-16 border-b border-border shrink-0">
      {brand.logo_url ? (
        <img src={resolveMedia(brand.logo_url)} alt="Love For Mankind" className="h-9 w-auto object-contain" />
      ) : (
        <div className="w-9 h-9 rounded-lg bg-primary flex items-center justify-center">
          <Heart className="w-5 h-5 text-primary-foreground" fill="currentColor" />
        </div>
      )}
      <div className="leading-tight">
        <div className="font-heading font-bold text-sm text-foreground">Love For Mankind</div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Operations OS</div>
      </div>
    </div>
  );
}

export default function AppShell() {
  const { user, logout, hasPerm } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    if (!hasPerm("notifications:view")) return;
    const fetchCount = () => api.get("/notifications-unread-count").then((r) => setUnread(r.data.count)).catch(() => {});
    fetchCount();
    const i = setInterval(fetchCount, 20000);
    return () => clearInterval(i);
  }, [hasPerm]);

  const doLogout = async () => {
    if (user?.role === "DRIVER") {
      const s = await api.get("/driver/work-sessions/current").then((r) => r.data.session).catch(() => null);
      if (s && !window.confirm("You're still on shift. Logging out will clock you out and stop location tracking. Continue?")) return;
    }
    await logout(); navigate("/login");
  };
  const initials = (user?.name || "U").split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div className="min-h-screen flex bg-background">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex flex-col w-64 border-r border-border bg-card shrink-0 h-screen sticky top-0">
        <Brand />
        <div className="flex-1 overflow-y-auto no-scrollbar"><NavList /></div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        {/* Topbar */}
        <header className="h-16 border-b border-border bg-card flex items-center justify-between px-4 sticky top-0 z-20">
          <div className="flex items-center gap-2">
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild className="lg:hidden">
                <Button variant="ghost" size="icon" data-testid="mobile-menu-btn"><Menu className="w-5 h-5" /></Button>
              </SheetTrigger>
              <SheetContent side="left" className="p-0 w-72 bg-card">
                <Brand />
                <NavList onNavigate={() => setOpen(false)} />
              </SheetContent>
            </Sheet>
            <span className="text-xs uppercase tracking-[0.18em] text-muted-foreground hidden sm:inline">
              Pursuing Exceptional Care
            </span>
          </div>

          <div className="flex items-center gap-1">
          {hasPerm("notifications:view") && (
            <Link to="/app/notifications" className="relative p-2 rounded-full hover:bg-accent transition-colors" data-testid="notif-bell">
              <Bell className="w-5 h-5 text-muted-foreground" />
              {unread > 0 && <span className="absolute top-1 right-1 min-w-[16px] h-4 px-1 rounded-full bg-primary text-primary-foreground text-[10px] font-bold flex items-center justify-center" data-testid="notif-count">{unread}</span>}
            </Link>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2 rounded-full pl-1 pr-2.5 py-1 hover:bg-accent transition-colors" data-testid="user-menu-btn">
                <span className="w-8 h-8 rounded-full bg-primary text-primary-foreground text-xs font-semibold flex items-center justify-center">{initials}</span>
                <span className="text-left hidden sm:block leading-tight">
                  <span className="block text-sm font-medium">{user?.name}</span>
                  <span className="block text-[11px] text-muted-foreground">{ROLE_LABEL[user?.role]}</span>
                </span>
                <ChevronDown className="w-4 h-4 text-muted-foreground" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div className="text-sm font-medium">{user?.name}</div>
                <div className="text-xs text-muted-foreground font-normal">{user?.email}</div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={doLogout} data-testid="logout-btn" className="text-danger">
                <LogOut className="w-4 h-4 mr-2" /> Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          </div>
        </header>

        <main className="flex-1 p-4 sm:p-6 lg:p-8 max-w-[1500px] w-full mx-auto"><Outlet /></main>
      </div>
    </div>
  );
}
